/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

/**
 * Product gallery eager-preload, 2026-09-11 (Open Work Items Item 8).
 *
 * Product pages render every color's gallery images into the DOM on load
 * (see sections/product-template.liquid), but each one uses lazysizes
 * (class "Image--lazyLoad", data-src instead of src) so the actual image
 * file only downloads once it scrolls into view or is otherwise unveiled.
 * That's correct for the default color's images, but it means any color a
 * shopper hasn't looked at yet has never been fetched. The first tap on a
 * different color triggers a brand new network fetch at that moment, which
 * reads as a visible lag while the image loads.
 *
 * Fix: once the browser goes idle after everything above-the-fold has
 * already loaded, force lazysizes to go ahead and load the rest of the
 * gallery images in the background, so by the time a shopper taps a
 * different color, the image is already cached rather than being fetched
 * live. This is additive only and does not touch the existing swap logic
 * in theme.min.js; if it ever needs to be rolled back, deleting this block
 * is enough.
 *
 * Uses lazysizes' own documented public API (window.lazySizes.loader.unveil)
 * to force-load an element regardless of viewport position. Confirmed this
 * is the real lazysizes library, not a custom fork, via the
 * window.lazySizesConfig object set in layout/theme.liquid, which matches
 * lazysizes' own expected global config name exactly.
 */
(function () {
  if (!/template-product/.test(document.body.className)) return;

  function preloadRemainingGalleryImages() {
    if (!window.lazySizes || !window.lazySizes.loader || typeof window.lazySizes.loader.unveil !== 'function') {
      return;
    }

    var lazyImages = document.querySelectorAll('.Product__Gallery img.Image--lazyLoad');

    lazyImages.forEach(function (img) {
      window.lazySizes.loader.unveil(img);
    });
  }

  if ('requestIdleCallback' in window) {
    requestIdleCallback(preloadRemainingGalleryImages, { timeout: 3000 });
  } else {
    setTimeout(preloadRemainingGalleryImages, 2000);
  }
})();

/**
 * Seasonal collections carousel toggle, 2026-09-19.
 *
 * sections/seasonal-collections-carousel.liquid is not one of the section
 * types theme.min.js registers (see the sections.register(...) calls in that
 * file), so there is no built-in JS driving its Flickity carousels or its
 * Fall/Halloween toggle buttons. This block does both by hand.
 *
 * Both panels' carousels are initialized on load, including the one that
 * starts hidden. Flickity measures its container's width at init time, and
 * an element hidden via "display: none" measures zero width, so the hidden
 * panel's carousel initializes broken. Dispatching a "resize" event right
 * after the panel is revealed forces Flickity to recalculate against the
 * now-visible container, which is why that dispatch has to happen first,
 * before anything else runs in the click handler.
 */
(function () {
  if (typeof Flickity === 'undefined') return;

  var sections = document.querySelectorAll('[data-section-type="seasonal-collections-carousel"]');

  sections.forEach(function (section) {
    section.querySelectorAll('[data-flickity-config]').forEach(function (carouselElement) {
      new Flickity(carouselElement, JSON.parse(carouselElement.getAttribute('data-flickity-config')));
    });

    var toggleButtons = section.querySelectorAll('[data-seasonal-toggle]');

    toggleButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        if (button.classList.contains('is-active')) {
          return;
        }

        var panelToShow = section.querySelector('#' + button.getAttribute('aria-controls'));

        toggleButtons.forEach(function (item) {
          item.classList.remove('is-active');
          item.setAttribute('aria-selected', 'false');
        });

        button.classList.add('is-active');
        button.setAttribute('aria-selected', 'true');

        section.querySelectorAll('[data-seasonal-panel]').forEach(function (panel) {
          panel.classList.add('is-hidden');
        });

        panelToShow.classList.remove('is-hidden');

        // Required: forces Flickity to recalculate now that the panel is visible (see comment above).
        window.dispatchEvent(new Event('resize'));
      });
    });
  });
})();

/**
 * Site-wide lazy-load fallback, 2026-09-19.
 *
 * lazySizes' own automatic viewport detection does not trigger anywhere
 * on this site, confirmed by direct testing: .Image--lazyLoad elements
 * never transition to .Image--lazyLoaded on their own, on any page.
 * Direct calls to window.lazySizes.loader.unveil() do work reliably,
 * confirmed by the existing gallery preload block above, which
 * successfully loads every product gallery image this exact way. This
 * block generalizes that same proven technique to every lazy image on
 * the site, using our own IntersectionObserver to call unveil() when an
 * image nears the viewport, preserving genuine lazy loading rather than
 * loading everything eagerly on page load.
 */
(function () {
  // Temporary diagnostic tool, not meant to be removed casually: append
  // ?skip_lazy_fallback=1 to a page URL to bypass this fallback entirely,
  // so we can check whether lazySizes' own viewport detection has started
  // working on its own without this fix masking the result.
  if (/[?&]skip_lazy_fallback=1(&|$)/.test(window.location.search)) return;

  if (!('IntersectionObserver' in window)) return;

  // lazysizes.min.js loads async and this file loads defer, so there's no
  // guarantee which finishes first; poll briefly for window.lazySizes to
  // be ready rather than giving up if we happen to run first.
  var POLL_INTERVAL_MS = 50;
  var POLL_TIMEOUT_MS = 2000;
  var elapsed = 0;

  function isLazySizesReady() {
    return !!(window.lazySizes && window.lazySizes.loader && typeof window.lazySizes.loader.unveil === 'function');
  }

  function waitForLazySizes() {
    if (isLazySizesReady()) {
      setup();
      return;
    }

    elapsed += POLL_INTERVAL_MS;
    if (elapsed >= POLL_TIMEOUT_MS) return;

    setTimeout(waitForLazySizes, POLL_INTERVAL_MS);
  }

  function setup() {
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          window.lazySizes.loader.unveil(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '200px 0px' });

    function observeAll() {
      document.querySelectorAll('img.Image--lazyLoad').forEach(function (img) {
        observer.observe(img);
      });
    }

    observeAll();

    // Catches images added after initial page load, e.g. AJAX cart updates.
    var mutationObserver = new MutationObserver(observeAll);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  waitForLazySizes();
})();
